/**
 * Admin 레이아웃 컴포넌트
 * 
 * 제휴사 Admin 구조를 참고하여 본사 Admin에 맞게 구현
 * 
 * 작성일: 2025-12-01
 */

import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';
import './AdminLayout.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * 페이지 제목 가져오기 (Header.tsx의 getPageTitle과 동일한 로직)
 */
const getPageTitle = (path: string): string => {
  const titleMap: { [key: string]: string } = {
    '/admin/dashboard': '대시보드',
    '/admin/bookings': '예약 관리',
    '/admin/bookings/calendar': '예약 캘린더',
    '/admin/tickets': '티켓 관리',
    '/admin/vouchers': '바우처 관리',
    '/admin/payments': '결제 관리',
    '/admin/settlements': '정산 관리',
    '/admin/settlements/agents': '에이전트 정산',
    '/admin/settlements/partners': '제휴업체 정산',
    '/admin/settlements/pg': 'PG사 정산',
    '/admin/agents': '에이전트 관리',
    '/admin/partners': '제휴업체 관리',
    '/admin/products': '상품 관리',
    '/admin/products/halls': '관 정보',
    '/admin/products/packages': '패키지 관리',
    '/admin/products/prices': '가격 관리',
    '/admin/promotions': '프로모션 관리',
    '/admin/fnb': 'F&B 관리',
    '/admin/employees': '직원 관리',
    '/admin/cctv': 'CCTV 관리',
    '/admin/content': '콘텐츠 관리',
    '/admin/content/notices': '공지사항',
    '/admin/content/faqs': 'FAQ',
    '/admin/content/events': '이벤트',
    '/admin/content/inquiries': '고객 문의',
    '/admin/admins': '관리자 관리',
    '/admin/admins/roles': '권한 그룹',
    '/admin/admins/permissions': '메뉴 권한',
    '/admin/admins/data-access-permissions': '데이터 접근 권한',
    '/admin/menus': '메뉴 관리',
    '/admin/menu-permissions': '메뉴 권한 설정',
    '/admin/reports': '통계 및 리포트',
  };
  
  // 동적 경로 처리 (예: /admin/bookings/:id)
  if (path.startsWith('/admin/bookings/') && path !== '/admin/bookings' && path !== '/admin/bookings/calendar') {
    return '예약 상세';
  }
  if (path.startsWith('/admin/payments/') && path !== '/admin/payments') {
    return '결제 상세';
  }
  if (path.startsWith('/admin/vouchers/') && path !== '/admin/vouchers') {
    return '바우처 상세';
  }
  if (path.startsWith('/admin/tickets/') && path !== '/admin/tickets') {
    return '티켓 상세';
  }
  if (path.startsWith('/admin/agents/') && path !== '/admin/agents') {
    return '에이전트 상세';
  }
  if (path.startsWith('/admin/partners/') && path !== '/admin/partners') {
    return '제휴업체 상세';
  }
  if (path.startsWith('/admin/products/halls/') && path !== '/admin/products/halls') {
    return '관 상세';
  }
  if (path.startsWith('/admin/products/packages/') && path !== '/admin/products/packages') {
    return '패키지 상세';
  }
  if (path.startsWith('/admin/promotions/') && path !== '/admin/promotions') {
    return '프로모션 상세';
  }
  if (path.startsWith('/admin/content/notices/') && path !== '/admin/content/notices') {
    return '공지사항 상세';
  }
  if (path.startsWith('/admin/content/faqs/') && path !== '/admin/content/faqs') {
    return 'FAQ 상세';
  }
  if (path.startsWith('/admin/content/events/') && path !== '/admin/content/events') {
    return '이벤트 상세';
  }
  if (path.startsWith('/admin/content/inquiries/') && path !== '/admin/content/inquiries') {
    return '문의 상세';
  }
  if (path.startsWith('/admin/admins/') && path !== '/admin/admins' && !path.startsWith('/admin/admins/roles') && !path.startsWith('/admin/admins/permissions') && !path.startsWith('/admin/admins/data-access-permissions')) {
    return '관리자 상세';
  }
  
  return titleMap[path] || '관리자';
};

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // 브라우저 탭 제목 동적 변경
  useEffect(() => {
    const appName = process.env.REACT_APP_APP_NAME || 'Lumin Art Admin';
    const menuTitle = getPageTitle(location.pathname);
    document.title = `${appName} - ${menuTitle}`;
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="admin-layout">
      <Header onMenuClick={toggleSidebar} />
      <div className="admin-layout-body">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <main className="admin-layout-main">
          <div className="admin-layout-content">
            <Container fluid className="admin-content-container">
              {children}
            </Container>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
